import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Text from '../../elements/Typo/Text'
import Container from '../../elements/Container'
import Icon from '../../components/icons'
import ExperimentStatusModal from '../../components/modal/ExperimentStatus.modal'

import AOS from 'aos'
import toast from 'react-hot-toast'
import KPISection from './_components/KPISection'
import InsightsDeepDiveSection from './_components/InsightDeepDive'
import SelfServiceInsights from './_components/selfServiceInsights'
import { SuccessApiResponse, ErrorResponse } from '../../store/api/apiTypes'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import { ExperimentDetailsI, experimentTitle, KPIDropDownOptionI, OptionI } from '../../_config/types'
import BlockingFactor from './_components/blockingFactor'
import { EXPERIMENT_PROGRESS_STATUS, URLS } from '../../_config'
import { useSelfServiceApi } from '../../store/api/hooks/selfServiceApiHook'
import Button from '../../elements/button'
import Loader from '../../components/loader'
import { ADVANCE_CONFIG_OPTION, EXPERIMENT_TYPES } from '../../_common/constant'
import InconclusiveReasonModal from '../../components/modal/InconclusiveReason.modal'
import { useUserSelector } from '../../store/selectors/userSelector'
import ExperimentCommentModal from '../../components/modal/ExperimentComment.modal'
import { useInsightFilterReport } from '../../store/api/hooks/insightFilterReportHook'
import { useInsightFilterReportSelector } from '../../store/selectors/insightFilterReportSelector'
import { InsightInputsReducerI } from '../../store/reducers/insightReportReducer'
import { useCommonApi } from '../../store/api/hooks/commonApiHook'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import ExperimentDetailsHeader from './_components/header'
import { trackEvent } from '../../_common/functions'
import { startNamedView } from '../../utils/analytics/rumTracking'
import i18n from '../../i18n'
import MTEKpiCards from './_components/KPISection/KPI/MTEKpiCards'
import DMLKpi from './_components/KPISection/KPI/DMLKpi'

type Tab = { id: 'experimentInsights' | 'blockingFactorInsights'; label: string; active: boolean }

type TabContentProps = { children: React.ReactNode }
interface CardI {
    kpi_unit?: string
    kpi_name: string
    kpi_impact_value: number
    uplift: number
    confidence_level: number
    experiment_status: EXPERIMENT_PROGRESS_STATUS.POSITIVE_LIFT | EXPERIMENT_PROGRESS_STATUS.NEGATIVE_LIFT | EXPERIMENT_PROGRESS_STATUS.INCONCLUSIVE | 'No_impact'
}
// TabContent.tsx

export interface AnalysisResponse {
    'Experiment Design': ExperimentDesign
    'Data Quality': DataQuality
    'Measurement Metrics': MeasurementMetrics
    'Measurement Outcome': MeasurementOutcome
    title: string
}

export interface ExperimentDesign {
    color: string
    contents: Content[]
}

export interface Content {
    text: string
    flag: boolean
    recommended_sample_size?: number
    actual_sample_size?: number
}

export interface DataQuality {
    color: string
    contents: Content2[]
}

export interface Content2 {
    text: string
    flag: boolean
    value: number
}

export interface MeasurementMetrics {
    color: string
    contents: Content3[]
}

export interface Content3 {
    text: string
    flag: boolean
    value: string
}

export interface MeasurementOutcome {
    color: string
    contents: Content4[]
}

export interface Content4 {
    text: string
    flag: boolean
    value: any
    minimum_uplift?: number
    actual_uplift?: number
}

export interface SelfServiceInsightsDataItem {
    chart_name: string
    Yaxis: string
    category: string
    additional_column: string
    filters: any
    metrix: string
}

export const advanceConfigOptions = [
    { label: ADVANCE_CONFIG_OPTION.SYNTHETIC_CONTROL.label, value: ADVANCE_CONFIG_OPTION.SYNTHETIC_CONTROL.value },
    { label: ADVANCE_CONFIG_OPTION.DID.label, value: ADVANCE_CONFIG_OPTION.DID.value },
]

export interface MeasurementAdvanceConfigI {
    [T: string]: { tooltip: string; type: string; value: number | boolean; label: string }
}

export interface ExperimentViewDesignI {
    analysisData: any
    experiment: any
    _handleShowExperimentStatus: () => void
}
export const ExperimentViewDesign: React.FC<ExperimentViewDesignI> = ({ analysisData, experiment, _handleShowExperimentStatus }) => {
    const { t } = useTranslation()
    return (
        <div className="col-span-1" data-aos="zoom-in" data-aos-delay="200">
            <div className="bg-[#F1F1F1] rounded-[0.625rem] py-0.5">
                <div className="flex flex-col gap-y-1 ml-3 py-2">
                    <div className="flex items-center gap-1">
                        <div style={{ backgroundColor: analysisData?.['Experiment Design']?.color || '#a52626' }} className="h-[0.417rem] w-[0.417rem] rounded-full font-normal"></div>
                        <Text size="xs">{t('Experiment Design')}</Text>
                    </div>
                    <div className="flex items-center gap-1">
                        <div style={{ backgroundColor: analysisData?.['Data Quality']?.color || '#a52626' }} className="p-[0.2rem] rounded-full font-normal"></div>
                        <Text size="xs">{t('Data Quality')}</Text>
                    </div>
                    <div className="flex items-center gap-1">
                        <div style={{ backgroundColor: analysisData?.['Measurement Metrics']?.color || '#a52626' }} className="p-[0.2rem] rounded-full font-normal"></div>
                        <Text size="xs">{t('Measurement Methodology')}</Text>
                    </div>
                    {experiment?.experiment_type?.title != EXPERIMENT_TYPES.MTE.title && (
                        <div className="flex items-center gap-1">
                            <div style={{ backgroundColor: analysisData?.['Measurement Outcome']?.color || '#a52626' }} className="p-[0.2rem] rounded-full font-normal"></div>
                            <div>
                                <Text size="xs">{t('Measurement Outcome')}</Text>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex justify-center mb-1">
                    <Text size="sm" className="underline cursor-pointer" onClick={_handleShowExperimentStatus}>
                        {t('View Details')}
                    </Text>
                </div>
            </div>
        </div>
    )
}

type GetExperimentDetailsFunction = (params: { literal: string; use_orm: boolean }, onSuccess: (resp: SuccessApiResponse<any>) => void, onError: (error: any) => void) => void

export const ExperimentDetailsAPICall = (
    exptLiteral: string,
    getExperimentDetails: GetExperimentDetailsFunction,
    setLoading: (loading: boolean) => void,
    setIsInvalidExptStatus: (invalid: boolean) => void,
    navigate: NavigateFunction,
    onSuccess?: (data: any) => void,
) => {
    getExperimentDetails(
        { literal: exptLiteral, use_orm: true },
        (resp: SuccessApiResponse<any>) => {
            const isInvalid = ![200, 300, 401, 402].includes(Number.parseInt(resp.data.status))
            setIsInvalidExptStatus(isInvalid)

            if (isInvalid) {
                void navigate(URLS.MY_EXPERIMENTS)
                toast((t) => <span className="text-yellow-600 text-[0.833rem]">{`Experiment ${resp.data.status_text}, this action can not be performed.`}</span>)
            }

            if (resp.success && onSuccess) {
                onSuccess(resp.data)
            }
            setLoading(false)
        },
        (error: any) => {
            if (error.status === 404) {
                toast.error(i18n.t("Oops! Sorry, the experiment that you are looking for is either deleted or doesn't exist"))
                void navigate(URLS.MY_EXPERIMENTS)
            } else {
                toast.error(error?.message)
                void navigate(URLS.MY_EXPERIMENTS)
            }
            setLoading(false)
        },
    )
}

const TabContent: React.FC<TabContentProps> = ({ children }) => <div className="bg-transparent">{children}</div>

const TabButtons: React.FC<{
    tabs: Tab[]
    activeTab: Tab['id']
    setActiveTab: React.Dispatch<React.SetStateAction<Tab['id']>>
}> = ({ tabs, activeTab, setActiveTab }) => {
    return (
        <>
            {tabs.map((tab) => (
                <button
                    type="button"
                    disabled={!tab.active}
                    key={tab.id}
                    onClick={() => {
                        trackEvent('experiment_details_tab_switched', { tab: tab.id })
                        setActiveTab(tab.id)
                    }}
                    className={`py-2 -mb-px relative
                        ${tab.active ? '' : 'opacity-50 cursor-not-allowed'}
                        ${activeTab === tab.id ? 'text-black-600 font-medium' : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'}`}
                >
                    <div className="flex flex-col gap-1 justify-between">
                        <Text className="text-[0.708rem] font-bold">{tab.label}</Text>
                        {activeTab === tab.id && (
                            <div className="bottom-[10px] flex justify-center w-full">
                                <div className=" left-0 w-14 h-[4px] border-t rounded-t-xl bg-[#F1B343] "></div>
                            </div>
                        )}
                    </div>
                </button>
            ))}
        </>
    )
}

interface ExperimentDeatilsProps {
    experiment: any
    mteCardData: any
    dmlCardData: any
    setExperiment?: React.Dispatch<React.SetStateAction<any>>
    setExperiments?: (data: any) => void
}

const ExperimentDetails: React.FC<ExperimentDeatilsProps> = ({ experiment, setExperiment, setExperiments, mteCardData, dmlCardData }) => {
    const { t } = useTranslation()
    const commonApi = useCommonApi()
    const lastScrollY = useRef(0)
    const commentModalRef: any = useRef()
    const contentRef = useRef<HTMLDivElement>(null)
    const { updateInsightFilter } = useInsightFilterReport()

    const [analysisData, setAnalysisData] = useState<AnalysisResponse>()

    const [isStatusModalModalOpen, setIsStatusModalModalOpen] = useState<boolean>(false)
    const [isInvalidExptStatus, setIsInvalidExptStatus] = useState<boolean>(true)
    const [isInconclusiveModalOpen, setIsInconclusiveModalOpen] = useState<boolean>(false)
    const [isNoImpactModalOpen, setIsNoImpactModalOpen] = useState<boolean>(false)
    const [groupOptions, setGroupOptions] = useState<OptionI[]>([])
    const [selectedGroup, setSelectedGroup] = useState<OptionI | null>(null)
    const [yAxisOptionData, setYAxisOptionData] = useState<OptionI[]>([])
    const [domainOptionData, setDomainOptionData] = useState<OptionI[]>([])
    const [selfInsightsDataLoading, setSelfInsightsDataLoading] = useState<boolean>(true)

    const { getExperimentDetails, getCardData, getDetailAndAnalysisData, getExperimentInsideMethodology } = useExperimentApi()
    const { getSelfServiceDropDown } = useSelfServiceApi()
    const experimentDetails: ExperimentDetailsI = experiment
    const filterData: InsightInputsReducerI = useInsightFilterReportSelector()

    const navigate = useNavigate()
    const [loading, setLoading] = useState<boolean>(true)
    const [headerExpend, setHeaderExpend] = useState<boolean>(true)
    const [selfServiceInsightsData, setSelfServiceInsightsData] = useState<SelfServiceInsightsDataItem[]>([])
    const [tabs, setTabs] = useState<Tab[]>([
        { id: 'experimentInsights', label: t('Experiment Insights'), active: true },
        { id: 'blockingFactorInsights', label: t('Blocking Factor Insights'), active: true },
    ])
    const [cardDetails, setCardDetails] = useState<CardI>({ kpi_name: '', kpi_impact_value: 0, uplift: 0, confidence_level: 0, experiment_status: EXPERIMENT_PROGRESS_STATUS.POSITIVE_LIFT })
    const [cardLoading, setCardLoading] = useState<boolean>(true)
    const [activeTab, setActiveTab] = useState<Tab['id']>('experimentInsights')
    const [selfSightInsightError, setSelfSightInsightError] = useState<string | null>(null)
    const [newSelfServiceInsights, setNewSelfServiceInsights] = useState<number>(0)
    const [dropdownOptions, setDropdownOptions] = useState<KPIDropDownOptionI[]>([])
    const [selectedKPI, setSelectedKPI] = useState<KPIDropDownOptionI | null>(null)
    const [selectedConfig, setSelectedConfig] = useState<{ label: string; value: string; blocking_factor_methodology?: string } | null>(null)
    const [selectedDateRange, setSelectedDateRange] = useState<any[]>([])
    const [cliper, setCliper] = useState<number | ''>('')
    const [nMatches, setNMatches] = useState<number | ''>('')
    const user: any = useUserSelector()
    const [experimentDetailsLoaded, setExperimentDetailsLoaded] = useState<boolean>(false)
    const [comments, setComments] = useState<any[]>([])
    const [experimentDataFetched, setExperimentDataFetched] = useState<boolean>(false)
    const [kpiImpactsData, setKpiImpactsData] = useState<any[]>([])
    const expSelfService = false

    const getAdvanceConfigOption = (type: 'measurement' | 'design') => {
        return new Promise((resolve, reject) => {
            commonApi.getAdvanceConfigOption(
                { sort_by: 'attribute_key', page: 1, per_page: 10, step: type },
                (resp: any) => {
                    resolve(resp.data?.result)
                },
                (error) => {
                    // Handle error
                },
            )
        })
    }

    const updateMeasurementConfig = (key: string, value: any) => {
        setMeasurementAdvanceConfig((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                value,
            },
        }))
    }

    const setMeasurementAdvanceConfiguration = (resp: any) => {
        const configKeys = new Set(['TREAT_OUTLIER', 'OUTLIER_THRESHOLD', 'ROLLING_MODE'])

        for (const item of resp.data?.result ?? []) {
            if (item?.value === '1') {
                item.value = true
            } else if (item?.value === '0') {
                item.value = false
            }
            if (configKeys.has(item.attribute_key)) {
                updateMeasurementConfig(item.attribute_key, item?.value)
            }
        }
    }

    const getMeasurementAdvanceConfiguration = () => {
        commonApi.getAdvanceConfigOption({ sort_by: 'attribute_key', page: 1, per_page: 10, step: 'measurement' }, setMeasurementAdvanceConfiguration, (error) => {
            toast.error(error?.message)
        })
    }

    const getDesignAdvanceConfiguration = () => {
        void getAdvanceConfigOption('design')
            .then((data: any) => {
                for (const item of data ?? []) {
                    if (item.attribute_key === 'CALIPER') {
                        setCliper(experimentDetails?.experiment_metadatas?.design_config?.CALIPER ?? item?.value)
                    }
                    if (item.attribute_key === 'N_MATCHES') {
                        setNMatches(experimentDetails?.experiment_metadatas?.design_config?.N_MATCHES ?? item?.value)
                    }
                }
            })
            .catch((error) => {
                console.error('Error :', error)
            })
    }

    const getExperimentInsideMethodologyCall = () => {
        return new Promise((resolve, reject) => {
            const is_mte = experimentDetails?.experiment_type?.title === EXPERIMENT_TYPES.MTE.title
            const payload: any = {
                literal: experimentDetails.experiment_literal,
                group: selectedGroup?.value,
                kpi: selectedKPI?.value,
                is_mte,
            }
            getExperimentInsideMethodology(
                payload,
                (resp: any) => {
                    if (resp.data.length > 1 && resp.data[0]?.category == 'did') {
                        const config: any = { label: resp.data[1]?.methodology, value: resp.data[1]?.methodology, blocking_factor_methodology: resp.data[1]?.category_blocking_factor }
                        setSelectedConfig(config)
                        resolve(config.value)
                    } else {
                        const config: any = { label: resp.data[0]?.methodology, value: resp.data[0]?.methodology, blocking_factor_methodology: resp.data[0]?.category_blocking_factor }
                        setSelectedConfig(config)
                        resolve(config.value)
                    }
                },
                (error: any) => {
                    console.error('Error fetching experiment methodology:', error)
                    reject(error)
                },
            )
        })
    }

    useEffect(() => {
        AOS.init({ once: false })
        AOS.refresh()
        startNamedView('Experiment Details')
        trackEvent('experiment_details_page_view', {
            experiment_literal: experimentDetails?.experiment_literal,
            experiment_name: experimentDetails?.experiment_name,
            experiment_type: experimentDetails?.experiment_type?.title,
        })
    }, [])

    useEffect(() => {
        if (experimentDetails) {
            setSelfServiceInsightsData(experimentDetails?.experiment_metadatas?.self_service_insight_filter ?? [])
            setNewSelfServiceInsights(experimentDetails?.experiment_metadatas?.self_service_insight_filter?.length ?? 0)
        }
    }, [experimentDetails])

    useEffect(() => {
        if (experimentDataFetched && selectedGroup && selectedKPI) {
            void getExperimentInsideMethodologyCall()
        }
    }, [selectedGroup, selectedKPI, experimentDataFetched])

    useEffect(() => {
        if (
            experimentDetails &&
            !isInvalidExptStatus &&
            selectedGroup &&
            selectedKPI &&
            experimentDataFetched &&
            selectedConfig?.value &&
            experimentDetails?.experiment_type?.title != EXPERIMENT_TYPES.MTE.title &&
            experiment?.experiment_type?.title != EXPERIMENT_TYPES.DML.title
        ) {
            _handleCardsApiCall(experimentDetails.experiment_literal, selectedGroup.value as any, selectedKPI.value, selectedDateRange, selectedConfig?.value)
                .then((resp) => {
                    setLoading(false)
                })
                .catch((err) => {
                    setLoading(false)
                })
        }
    }, [experimentDetails, isInvalidExptStatus, selectedConfig, selectedDateRange])

    useEffect(() => {
        if (
            experimentDetails &&
            !isInvalidExptStatus &&
            //selectedGroup &&
            selectedKPI &&
            experimentDataFetched &&
            selectedConfig?.value
            // experimentDetails?.experiment_type?.title != EXPERIMENT_TYPES.MTE.title &&
            // experimentDetails?.experiment_type?.title != EXPERIMENT_TYPES.DML.title
        ) {
            _handleDetailsAndAnalysisCall(experimentDetails.experiment_literal, (selectedGroup?.value as any) || 'Test', selectedKPI.value, selectedConfig?.value)
                .then((resp) => {
                    setLoading(false)
                })
                .catch((err) => {
                    setLoading(false)
                })
        }
    }, [experimentDetails, isInvalidExptStatus, selectedConfig])

    const takeConfigDicision = () => {
        const options = experimentDetails?.experiment_metadatas?.metrices.map((metric: any) => {
            return {
                label: metric?.literral_value,
                value: metric?.literral_value,
                kpi_type: metric?.kpi_type,
                ...metric,
            }
        })
        if (experimentDetails?.experiment_type?.title == EXPERIMENT_TYPES.MTE.title) {
            const config: any = { label: 'Test', value: 'Test', blocking_factor_methodology: null }
            setSelectedConfig(config)
        }
        if (experimentDetails?.experiment_type?.title == 'Post-Treatment Comparison') {
            //setSelectedConfig(advanceConfigOptions[1])
        } else if (options.length > 0) {
            setSelectedKPI(options[0])
        }

        setDropdownOptions(options)
    }

    const onExperimentLoad = () => {
        if (experimentDetailsLoaded) return
        setExperimentDetailsLoaded(true)
        if (experimentDetails?.experiment_metadatas?.interventions[0]?.group_configs) {
            const options = experimentDetails?.experiment_metadatas?.interventions[0]?.group_configs
                .filter((data: any) => !data['is_control'])
                .sort((a: any, b: any) => a.name.localeCompare(b.name))
                .map((data: any) => ({ label: data?.name, value: data?.name }))
            setGroupOptions(options)
            setSelectedGroup(options[0])
        }
        if (experimentDetails?.experiment_metadatas?.metrices) {
            takeConfigDicision()
        }

        if (experimentDetails?.experiment_metadatas?.measurement_config == null) {
            getMeasurementAdvanceConfiguration()
        } else {
            setMeasurementAdvanceConfig({
                TREAT_OUTLIER: {
                    tooltip: t('Detects and adjusts for outliers in the treatment group to avoid skewed results.'),
                    value: experimentDetails?.experiment_metadatas?.measurement_config.TREAT_OUTLIER,
                    type: 'switch',
                    label: t('Treat Outlier'),
                },
                OUTLIER_THRESHOLD: {
                    tooltip: t('Controls the sensitivity of outlier detection - sets the percentage of POCs (stores) that will be flagged as outliers and removed from analysis (e.g., 0.005 = 0.5% of stores will be marked as outliers).'),
                    value: experimentDetails?.experiment_metadatas?.measurement_config.OUTLIER_THRESHOLD,
                    type: 'numericStepper',
                    label: t('Outlier Threshold'),
                },
                ROLLING_MODE: {
                    tooltip: t('Enables rolling window-based KPI measurement to capture delayed or sustained impact.'),
                    value: experimentDetails?.experiment_metadatas?.measurement_config.ROLLING_MODE,
                    type: 'switch',
                    label: t('Rolling Mode'),
                },
            })
        }

        if (experimentDetails?.experiment_type?.title != 'RCT' && !experimentDetails?.is_measurement_flow && experimentDetails?.experiment_metadatas?.design_configs == null) {
            getDesignAdvanceConfiguration()
        }
        setLoading(false)
    }

    useEffect(() => {
        if (experimentDetails && (mteCardData || dmlCardData)) {
            updateTabsByStatus(experimentDetails?.experiment_result || '', true)
        }
    }, [experimentDetails, mteCardData, dmlCardData])

    useEffect(() => {
        if (experimentDetails) {
            setExperimentDataFetched(true)
            setIsInvalidExptStatus(false)
            onExperimentLoad()
        }
    }, [experimentDetails])

    const renderTabContent = (tabId: Tab['id']): React.ReactNode => {
        if (selectedConfig?.value == null) return null
        const experimentTypeTitle: experimentTitle = (experimentDetails?.experiment_type?.title as experimentTitle) ?? null
        const isMteExperiment = experimentTypeTitle === EXPERIMENT_TYPES.MTE.title
        const isDmlExperiment = experimentTypeTitle === EXPERIMENT_TYPES.DML.title
        const showGenericInsights = !isMteExperiment && !isDmlExperiment
        switch (tabId) {
            case 'experimentInsights':
                return (
                    <TabContent>
                        {experimentDetails && showGenericInsights && (
                            <>
                                <KPISection
                                    setKpiImpactsData={setKpiImpactsData}
                                    experimentDetails={experimentDetails}
                                    selectedGroup={(selectedGroup?.value as any) || null}
                                    selectedKPI={selectedKPI}
                                    selectedOption={selectedConfig?.value}
                                    selectedDateRange={selectedDateRange}
                                />
                                {!isDmlExperiment && (
                                    <InsightsDeepDiveSection
                                        cardDetails={cardDetails}
                                        selectedGroup={(selectedGroup?.value as any) || null}
                                        kpi_literal={selectedKPI?.kpi_literal ?? null}
                                        kpi_value={selectedKPI?.value ?? null}
                                        kpi_unit={selectedKPI?.kpi_unit ?? null}
                                        selectedDateRange={selectedDateRange}
                                        selectedOption={selectedConfig?.value}
                                    />
                                )}
                            </>
                        )}

                        {experimentDetails && isMteExperiment && (
                            <MTEKpiCards
                                experimentDetails={experimentDetails}
                                selectedGroup={(selectedGroup?.value as any) || null}
                                selectedKPI={selectedKPI}
                                selectedDateRange={selectedDateRange}
                                selectedOptionConfig={selectedConfig?.value}
                                toDownloadAsReport={true}
                                reportError={reportError}
                                methodology={selectedConfig?.value ?? null}
                                mteCardData={mteCardData}
                            />
                        )}
                        {experimentDetails && isDmlExperiment && (
                            <DMLKpi
                                experimentDetails={experimentDetails}
                                selectedGroup={(selectedGroup?.value as any) || null}
                                selectedKPI={selectedKPI}
                                selectedDateRange={selectedDateRange}
                                selectedOptionConfig={selectedConfig?.value}
                                toDownloadAsReport={true}
                                reportError={reportError}
                                // methodology={selectedConfig?.value ?? null}
                                // mteCardData={mteCardData}
                                dmlCardData={dmlCardData}
                            />
                        )}
                        <div className="hidden">
                            {!selfInsightsDataLoading && expSelfService && (
                                <>
                                    {selfServiceInsightsData?.map((data: any, index: number) => (
                                        <SelfServiceInsights
                                            key={data?.id + index}
                                            compKey={index + 1}
                                            data={data}
                                            selectedGroup={(selectedGroup?.value as any) || null}
                                            yAxisOptions={yAxisOptionData}
                                            domainOptions={domainOptionData}
                                            errorMsg={selfSightInsightError}
                                            methodology={selectedConfig?.value}
                                            selectedKPI={selectedKPI?.value}
                                        />
                                    ))}
                                    {selfServiceInsightsData?.length < newSelfServiceInsights || !selfServiceInsightsData?.length ? (
                                        <SelfServiceInsights
                                            compKey={selfServiceInsightsData?.length + 1}
                                            data={{ chart_name: '', Yaxis: yAxisOptionData?.[0]?.value || '', category: '', additional_column: '', filters: '', metrix: '' }}
                                            selectedGroup={(selectedGroup?.value as any) || null}
                                            yAxisOptions={yAxisOptionData}
                                            domainOptions={domainOptionData}
                                            methodology={selectedConfig?.value}
                                            selectedKPI={selectedKPI?.value}
                                            isSaveEnable={true}
                                            onHandleSave={() => {
                                                setNewSelfServiceInsights(0)
                                                if (experiment.experiment_literal) {
                                                    _handleDetailsExperimentsApiCall(experiment.experiment_literal)
                                                }
                                            }}
                                            errorMsg={selfSightInsightError}
                                        />
                                    ) : (
                                        <Button
                                            size="sm"
                                            className="w-full border-2 border-gray-300 rounded-lg"
                                            theme="light"
                                            onClick={() => {
                                                trackEvent('experiment_details_add_self_service_insight', { experiment_literal: experimentDetails?.experiment_literal })
                                                setNewSelfServiceInsights(selfServiceInsightsData?.length + 1)
                                            }}
                                        >
                                            <Icon name="goPlus" size={1} />
                                            {t('Add Another Self Service Insight')}
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    </TabContent>
                )
            case 'blockingFactorInsights':
                return (
                    <TabContent>
                        <BlockingFactor selectedGroup={(selectedGroup?.value as any) ?? null} selectedConfig={selectedConfig?.blocking_factor_methodology} selectedKPI={selectedKPI?.value ?? null} selectedDateRange={selectedDateRange} />
                    </TabContent>
                )
        }
    }

    const _handleShowExperimentStatus = () => {
        setIsStatusModalModalOpen(true)
    }

    const _handleDetailsExperimentsApiCall = (exptLiteral: string) => {
        ExperimentDetailsAPICall(exptLiteral, getExperimentDetails, setLoading, setIsInvalidExptStatus, navigate, (data: any) => {
            setSelfServiceInsightsData(data?.experiment_metadatas?.self_service_insight_filter ?? [])
            setNewSelfServiceInsights(data?.experiment_metadatas?.self_service_insight_filter?.length ?? 0)
        })
    }

    const kpiFilter = (resp: any) => {
        return experiment?.experiment_metadatas?.metrices?.find((metric: any) => metric.literral_value === resp.data.kpi_name)
    }

    const updateTabsByStatus = (status: string, is_mte_or_dml: boolean = false) => {
        if (selectedConfig?.blocking_factor_methodology && status.search('Inconclusive') === -1) {
            setTabs(tabs.map((tab) => ({ ...tab, active: true })))
        } else if (is_mte_or_dml) {
            setTabs(tabs.map((tab) => (tab.id === 'blockingFactorInsights' ? { ...tab, active: false } : tab)))
            if (activeTab === 'blockingFactorInsights') {
                setActiveTab('experimentInsights')
            }
        } else {
            setTabs(tabs.map((tab) => (tab.id === 'blockingFactorInsights' ? { ...tab, active: false } : tab)))
            if (activeTab === 'blockingFactorInsights') {
                setActiveTab('experimentInsights')
            }
        }
    }

    const buildCardParams = (literal: string, group_name: string, selectedKp: string, selectedDateRange: any[], selectedConf?: string) => {
        const params: any = { literal, group_name, kpiname: selectedKp }
        if (selectedConfig != undefined) {
            params.methodology = selectedConf
        }
        if (selectedDateRange.length == 2) {
            params.measurement_start_date = selectedDateRange[0]
            params.measurement_end_date = selectedDateRange[1]
            params.is_slice_and_dice = true
        } else {
            params.measurement_start_date = null
            params.measurement_end_date = null
            params.is_slice_and_dice = false
        }
        return params
    }

    const handleCardDataSuccess = (resp: SuccessApiResponse<CardI>, resolve: (value: unknown) => void) => {
        const KPI: any = kpiFilter(resp)
        const UNIT = KPI ? KPI.kpi_unit : ''
        setCardDetails({ ...resp.data, kpi_unit: UNIT })
        updateTabsByStatus(resp.data.experiment_status)
        resolve(resp.data)
    }

    const _handleCardsApiCall = async (literal: string, group_name: string, selectedKp: string, selectedDateRange: any[], selectedConf?: string) => {
        setCardLoading(true)
        return new Promise((resolve, reject) => {
            const params = buildCardParams(literal, group_name, selectedKp, selectedDateRange, selectedConf)
            getCardData(
                params,
                (resp: SuccessApiResponse<CardI>) => {
                    setCardLoading(false)
                    handleCardDataSuccess(resp, resolve)
                },
                (error: ErrorResponse<CardI[]>) => {
                    setCardLoading(false)
                    toast.error(error?.message || t('Something went wrong'))
                },
            )
        })
    }

    const _handleDetailsAndAnalysisCall = async (literal: string, group_name: string, selectedKp: string, selectedConf?: string) => {
        return new Promise((resolve, reject) => {
            const payload: any = {
                literal,
                group_name,
                kpiname: selectedKp,
            }

            if (selectedConfig != null) {
                payload.methodology = selectedConf
            }

            const SC: any = experimentDetails.success_criteria.find((criteria: any) => criteria.kpi_literal === selectedKPI?.kpi_literal)

            getDetailAndAnalysisData(
                payload,
                (resp: SuccessApiResponse<any>) => {
                    if (SC) {
                        resp.data['Measurement Outcome'].contents[1].desired_statistical_significance = SC.confidence_level
                        resp.data['Measurement Outcome'].contents[2].minimum_uplift = SC.minimum_uplift
                    } else {
                        resp.data['Measurement Outcome'].contents[1].desired_statistical_significance = experimentDetails.success_criteria[0]?.confidence_level
                        resp.data['Measurement Outcome'].contents[2].minimum_uplift = experimentDetails.success_criteria[0]?.minimum_uplift
                    }

                    setAnalysisData(resp.data)
                    resolve(resp.data)
                },
                (error: ErrorResponse<any>) => {
                    toast.error(t(error.message))
                    // reject(new Error(error.message))
                },
            )
        })
    }

    const [MeasurementAdvanceConfig, setMeasurementAdvanceConfig] = useState<MeasurementAdvanceConfigI>({
        TREAT_OUTLIER: {
            tooltip: t('Detects and adjusts for outliers in the treatment group to avoid skewed results.'),
            value: false,
            type: 'switch',
            label: t('Treat Outlier'),
        },
        OUTLIER_THRESHOLD: {
            tooltip: t('Controls the sensitivity of outlier detection - sets the percentage of POCs (stores) that will be flagged as outliers and removed from analysis (e.g., 0.005 = 0.5% of stores will be marked as outliers).'),
            value: 0.005,
            type: 'numericStepper',
            label: t('Outlier Threshold'),
        },
        ROLLING_MODE: {
            tooltip: t('Enables rolling window-based KPI measurement to capture delayed or sustained impact.'),
            value: false,
            type: 'switch',
            label: t('Rolling Mode'),
        },
    })

    const stopSelfService: boolean = false

    useEffect(() => {
        if ((selectedGroup || selectedConfig) && experimentDataFetched && stopSelfService) {
            getSelfServiceDropDown(
                // eslint-disable-next-line object-shorthand
                { literal: experiment.experiment_literal, group_name: selectedGroup, methodology: selectedConfig?.value, kpiname: selectedKPI },
                (data: any) => {
                    const yAxisOptions = data.data?.numeric_columns?.map((item: any) => {
                        return { label: item, value: item }
                    })
                    setYAxisOptionData(yAxisOptions)

                    const domainOptions = data.data.string_columns?.map((item: any) => {
                        return { label: item, value: item }
                    })
                    setDomainOptionData(domainOptions)
                    setSelfInsightsDataLoading(false)
                },
                (error: ErrorResponse<[]>) => {
                    setSelfInsightsDataLoading(false)
                    setSelfSightInsightError(error.message)
                },
            )
        }
    }, [selectedGroup, selectedConfig, experimentDataFetched])

    useEffect(() => {
        updateInsightFilter({
            ...filterData,
            selectedConfig: selectedConfig ?? null,
        })
    }, [selectedConfig])

    useEffect(() => {
        updateInsightFilter({
            ...filterData,
            selectedDateRange,
        })
    }, [selectedDateRange])

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY

            if (contentRef.current) {
                const contentRect = contentRef.current?.getBoundingClientRect()
                if (Number.parseInt('' + (contentRect.top - 100)) < 0 && scrollY > lastScrollY.current) {
                    setHeaderExpend(false)
                } else if (Number.parseInt('' + (contentRect.top - 100)) > 0 && scrollY < lastScrollY.current) {
                    setHeaderExpend(true)
                }
            }

            // Store the current scroll position
            lastScrollY.current = scrollY
        }

        // Add scroll event listener with throttling for performance
        let ticking = false
        const scrollListener = () => {
            if (!ticking) {
                globalThis.requestAnimationFrame(() => {
                    handleScroll()
                    ticking = false
                })
                ticking = true
            }
        }

        window.addEventListener('scroll', scrollListener, { passive: true })

        // Cleanup
        return () => {
            window.removeEventListener('scroll', scrollListener)
        }
    }, [headerExpend])

    const openComment = () => {
        commentModalRef?.current?.showModal()
    }

    const getCStatus = (prop: any) => {
        if (prop.type === 'switch' && prop.value) {
            return t('True')
        } else if (prop.type === 'numericStepper') {
            return ''
        }
        return t('False')
    }

    if (loading) {
        return <Loader />
    }

    return (
        <div data-testid="experiment-details">
            {!isInvalidExptStatus && experimentDataFetched && (
                <>
                    <Container className="relative">
                        <ExperimentDetailsHeader
                            kpiImpactsData={kpiImpactsData}
                            experimentDetails={experimentDetails}
                            groupOptions={groupOptions}
                            selectedGroup={selectedGroup}
                            setSelectedGroup={setSelectedGroup}
                            dropdownOptions={dropdownOptions}
                            selectedKPI={selectedKPI}
                            setSelectedKPI={setSelectedKPI}
                            setSelectedDateRange={setSelectedDateRange}
                            selectedDateRange={selectedDateRange}
                            setSelectedConfig={setSelectedConfig}
                            user={user}
                            comments={comments}
                            openComment={openComment}
                            selectedConfig={selectedConfig}
                            nMatches={nMatches}
                            cliper={cliper}
                            MeasurementAdvanceConfig={MeasurementAdvanceConfig}
                            getCStatus={getCStatus}
                            cardDetails={cardDetails}
                            cardLoading={cardLoading}
                            analysisData={analysisData}
                            mteCardData={mteCardData}
                            dmlCardData={dmlCardData}
                            _handleShowExperimentStatus={_handleShowExperimentStatus}
                            setIsInconclusiveModalOpen={setIsInconclusiveModalOpen}
                            setIsNoImpactModalOpen={setIsNoImpactModalOpen}
                            toDownloadAsReport={false}
                            onUpdate={() => {
                                ExperimentDetailsAPICall(experiment.experiment_literal || '', getExperimentDetails, setLoading, setIsInvalidExptStatus, navigate, (data: any) => {
                                    setExperiment?.(data)
                                    setExperiments?.(data)
                                    //setSelectedKPI(data?.experiment_metadatas?.metrices?.find((metric: any) => metric.kpi_literal === selectedKPI?.kpi_literal) || null);
                                    if (selectedConfig?.value && experiment?.experiment_type?.title != EXPERIMENT_TYPES.MTE.title && experiment?.experiment_type?.title != EXPERIMENT_TYPES.DML.title) {
                                        _handleCardsApiCall(experiment.experiment_literal, (selectedGroup?.value as any) || '', selectedKPI?.value || '', selectedDateRange, selectedConfig?.value)
                                            .then((resp) => {
                                                setLoading(false)
                                            })
                                            .catch((err) => {
                                                setLoading(false)
                                            })
                                    }
                                })
                            }}
                        />

                        <div ref={contentRef}>
                            <div className="flex justify-between px-3">
                                <div className="flex items-start gap-7">
                                    <TabButtons tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
                                </div>
                            </div>

                            {experimentDataFetched && selectedConfig && (
                                <div className="px-3">
                                    <div style={{ minHeight: 'calc(100vh - 8.41rem)' }}>{renderTabContent(activeTab)}</div>
                                </div>
                            )}
                        </div>
                    </Container>
                    {(isStatusModalModalOpen && (
                        <ExperimentStatusModal
                            isModalOpen={isStatusModalModalOpen}
                            onClose={() => setIsStatusModalModalOpen(false)}
                            data={analysisData}
                            experiment_literal={experimentDetails?.experiment_literal}
                            experiment_status={experimentDetails?.status_text}
                            experimentDetails={experimentDetails}
                        />
                    )) || <></>}
                    {(isInconclusiveModalOpen || isNoImpactModalOpen) && (
                        <InconclusiveReasonModal
                            literal={experimentDetails.experiment_literal}
                            group_name={(selectedGroup?.value || null) as any}
                            kpi={selectedKPI?.value || null}
                            methodology={selectedConfig?.value || null}
                            Reason={null}
                            isModalOpen={isInconclusiveModalOpen || isNoImpactModalOpen}
                            onClose={() => {
                                setIsInconclusiveModalOpen(false)
                                setIsNoImpactModalOpen(false)
                            }}
                            type={isNoImpactModalOpen ? 'no_impact' : 'inconclusive'}
                        />
                    )}
                </>
            )}

            <ExperimentCommentModal
                success={() => {
                    commentModalRef?.current?.close()
                }}
                _ref={commentModalRef}
                updateComments={setComments}
            />
        </div>
    )
}

export default ExperimentDetails
